import { CheckCircle2, Clock3, XCircle } from "lucide-react";

const PLANS = [
  { key: "starter", label: "Starter", price: "€499/mo" },
  { key: "creator", label: "Creator", price: "€699/mo" },
  { key: "business", label: "Business", price: "€899/mo", popular: true },
  { key: "agency", label: "Agency", price: "€1,199/mo" },
  { key: "enterprise", label: "Enterprise", price: "from €2,499/mo" },
];

const yes = (label = "Included") => ({ kind: "yes", label });
const no = { kind: "no", label: "Not included" };
const value = (label) => ({ kind: "value", label });
const optional = (label) => ({ kind: "optional", label });
const soon = (label = "Coming soon") => ({ kind: "soon", label });

const ROWS = [
  { feature: "Users", starter: value("8"), creator: value("15"), business: value("25"), agency: value("50"), enterprise: value("Unlimited") },
  { feature: "Company workspaces", starter: value("1"), creator: value("1"), business: value("3"), agency: value("5"), enterprise: value("By Enterprise tier") },
  { feature: "AI assistants on Claude & Gemini", starter: yes(), creator: yes(), business: yes(), agency: yes(), enterprise: yes() },
  { feature: "Planning & time tracking", starter: yes("Core"), creator: yes("Full"), business: yes("Full"), agency: yes("Full"), enterprise: yes("Full") },
  { feature: "Communication & documents", starter: yes(), creator: yes(), business: yes(), agency: yes(), enterprise: yes() },
  { feature: "Email & social marketing", starter: yes("Included"), creator: yes("Advanced"), business: yes("Advanced"), agency: yes("Multi-client"), enterprise: yes("Full") },
  { feature: "AI photo, content & video", starter: yes("Included"), creator: yes("Creative suite"), business: yes("Full"), agency: yes("Multi-brand"), enterprise: yes("Full") },
  { feature: "Canva integration", starter: yes(), creator: yes(), business: yes(), agency: yes(), enterprise: yes() },
  { feature: "Website Builder templates", starter: yes("100"), creator: yes("100"), business: yes("100"), agency: yes("100"), enterprise: yes() },
  { feature: "Full 250-template library", starter: optional("+€4.99/mo"), creator: optional("+€4.99/mo"), business: optional("+€4.99/mo"), agency: optional("+€4.99/mo"), enterprise: yes() },
  { feature: "Hosting, SSL & basic SEO", starter: yes(), creator: yes(), business: yes(), agency: yes(), enterprise: yes() },
  { feature: "Connect an existing domain", starter: yes("Free"), creator: yes("Free"), business: yes("Free"), agency: yes("Free"), enterprise: yes("Free") },
  { feature: "CRM & sales", starter: yes("Core"), creator: yes("Full"), business: yes("Full"), agency: yes("Multi-client"), enterprise: yes("Full") },
  { feature: "Finance & invoicing", starter: yes("Core"), creator: yes("Full"), business: yes("Full"), agency: yes("Multi-client"), enterprise: yes("Full") },
  { feature: "Projects", starter: yes("Core"), creator: yes("Core"), business: yes("Full"), agency: yes("Multi-client"), enterprise: yes("Full") },
  { feature: "Accounting", starter: yes("Core"), creator: yes("Core"), business: yes("Full"), agency: yes("Full"), enterprise: yes("Full") },
  { feature: "Operations & inventory", starter: no, creator: no, business: yes(), agency: yes(), enterprise: yes() },
  { feature: "Integrated webshop", starter: soon(), creator: soon(), business: soon("Advanced · coming soon"), agency: soon("Multi-store · coming soon"), enterprise: soon("Full · coming soon") },
  { feature: "HR & people", starter: no, creator: no, business: no, agency: no, enterprise: yes() },
  { feature: "Compliance & full ERP", starter: no, creator: no, business: no, agency: no, enterprise: yes() },
  { feature: "SSO, audit & enterprise policies", starter: no, creator: no, business: no, agency: no, enterprise: yes() },
];

function Cell({ cell }) {
  if (cell.kind === "yes") {
    return (
      <span className="inline-flex items-center justify-center gap-1.5 text-[12.5px] font-semibold text-emerald-700">
        <CheckCircle2 size={16} aria-hidden="true" />
        <span>{cell.label}</span>
      </span>
    );
  }
  if (cell.kind === "no") {
    return (
      <span className="inline-flex items-center justify-center gap-1.5 text-[12.5px] font-semibold text-red-600">
        <XCircle size={16} aria-hidden="true" />
        <span className="sr-only">{cell.label}</span>
      </span>
    );
  }
  if (cell.kind === "soon") {
    return (
      <span className="inline-flex items-center justify-center gap-1.5 text-[12px] font-semibold text-amber-700">
        <Clock3 size={15} aria-hidden="true" />
        <span>{cell.label}</span>
      </span>
    );
  }
  if (cell.kind === "optional") {
    return <span className="text-[12.5px] font-semibold text-[#202B60]">{cell.label}</span>;
  }
  return <span className="text-[13px] font-semibold text-[#0A1628]">{cell.label}</span>;
}

export default function SubscriptionFeatureMatrix() {
  return (
    <section className="zy-section bg-[#FAFAFB]" data-testid="subscription-feature-matrix">
      <div className="zy-container">
        <div className="max-w-3xl mx-auto text-center mb-10">
          <p className="zy-eyebrow mb-3">Compare subscriptions</p>
          <h2 className="zy-h2">Choose the level that fits your business</h2>
          <p className="zy-body mt-4">
            Every plan includes Zynthoro&apos;s AI foundation. Higher plans add more users,
            workspaces and operational depth.
          </p>
        </div>

        <div className="hidden lg:block w-full max-w-full overflow-x-auto rounded-2xl border border-[#e8ebf2] bg-white shadow-sm">
          <table className="w-full min-w-[1040px]" data-testid="subscription-feature-table">
            <thead>
              <tr className="border-b border-[#e8ebf2]">
                <th className="sticky left-0 z-20 min-w-[250px] bg-[#202B60] px-5 py-5 text-left text-[12px] font-semibold uppercase tracking-wider text-white">
                  Feature
                </th>
                {PLANS.map((plan) => (
                  <th
                    key={plan.key}
                    className="relative min-w-[155px] bg-[#202B60] px-4 py-5 text-center text-white"
                  >
                    {plan.popular && (
                      <span className="absolute left-1/2 top-1 -translate-x-1/2 rounded-full bg-[#D4AF37] px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[#332800]">
                        Most chosen
                      </span>
                    )}
                    <span className={`block text-[14px] font-bold ${plan.popular ? "mt-3" : ""}`}>{plan.label}</span>
                    <span className="mt-1 block text-[11.5px] font-normal text-white/75">{plan.price}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row, index) => (
                <tr key={row.feature} className={index % 2 ? "bg-[#FAFAFB]" : "bg-white"}>
                  <th className={`sticky left-0 z-10 border-r border-[#eef0f5] px-5 py-3.5 text-left text-[13px] font-semibold text-[#0A1628] ${index % 2 ? "bg-[#FAFAFB]" : "bg-white"}`}>
                    {row.feature}
                  </th>
                  {PLANS.map((plan) => (
                    <td key={plan.key} className="border-l border-[#f0f1f5] px-3 py-3.5 text-center align-middle">
                      <Cell cell={row[plan.key]} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="space-y-4 lg:hidden" data-testid="subscription-feature-cards">
          {PLANS.map((plan) => (
            <article key={plan.key} className="overflow-hidden rounded-2xl border border-[#e8ebf2] bg-white shadow-sm">
              <header className="relative bg-[#202B60] px-5 py-5 text-white">
                {plan.popular && (
                  <span className="absolute right-4 top-4 rounded-full bg-[#D4AF37] px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[#332800]">
                    Most chosen
                  </span>
                )}
                <h3 className="text-[17px] font-bold">{plan.label}</h3>
                <p className="mt-1 text-[12px] text-white/75">{plan.price}</p>
              </header>
              <dl>
                {ROWS.map((row, index) => (
                  <div key={row.feature} className={`grid grid-cols-[minmax(0,1fr)_minmax(110px,auto)] items-center gap-3 px-4 py-3 ${index % 2 ? "bg-[#FAFAFB]" : "bg-white"}`}>
                    <dt className="min-w-0 text-[12.5px] font-medium text-[#0A1628]">{row.feature}</dt>
                    <dd className="min-w-0 text-right"><Cell cell={row[plan.key]} /></dd>
                  </div>
                ))}
              </dl>
            </article>
          ))}
        </div>

        <div className="mt-4 flex flex-wrap justify-center gap-x-6 gap-y-2 text-[11.5px] text-[#666]">
          <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={14} className="text-emerald-700" /> Included</span>
          <span className="inline-flex items-center gap-1.5"><XCircle size={14} className="text-red-600" /> Not included</span>
          <span className="inline-flex items-center gap-1.5"><Clock3 size={14} className="text-amber-700" /> Coming soon</span>
        </div>
        <p className="mt-3 text-center text-[11.5px] text-[#777]">
          Kickstart plans and one-time credit additions are separate and remain unchanged.
        </p>
      </div>
    </section>
  );
}
